import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FaBan, FaExchangeAlt } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';
import './AdminReviews.scss';

type ReviewTaskCommentDto = {
  guid: string;
  commentText: string;
  createdByEmail: string;
  createdDate: string;
};

type ReviewTaskDto = {
  guid: string;
  taskName: string;
  taskDescription?: string;
  taskConfig?: string;
  sortOrder: number;
  isRequired: boolean;
  isComplete: boolean;
  completedDate?: string;
  commentCount: number;
  comments: ReviewTaskCommentDto[];
};

type AdminReviewDetailDto = {
  guid: string;
  reviewName: string;
  templateName: string;
  templateCategory: string;
  templateDescription?: string;
  reviewStatus: string;
  assignedTo: string;
  assignedBy: string;
  assignedDate?: string;
  dueDate?: string;
  reviewDetails?: string;
  reviewConfig?: string;
  isComplete: boolean;
  completedDate?: string;
  tasks: ReviewTaskDto[];
};

const AdminReviewDetail: React.FC = () => {
  const { reviewGuid } = useParams<{ reviewGuid: string }>();
  const navigate = useNavigate();

  const [review, setReview] = useState<AdminReviewDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [newAssigneeEmail, setNewAssigneeEmail] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [expandedTaskGuid, setExpandedTaskGuid] = useState<string | null>(null);

  const canMutate = useMemo(() => !!review && !review.isComplete, [review]);

  useEffect(() => {
    if (!reviewGuid) return;
    void loadReview();
  }, [reviewGuid]);

  const loadReview = async () => {
    if (!reviewGuid) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<AdminReviewDetailDto>(`/api/admin/reviewmanagement/reviews/${reviewGuid}`);
      setReview(response.data);
      setNewAssigneeEmail(response.data.assignedTo ?? '');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load review detail');
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reviewGuid || !newAssigneeEmail.trim()) {
      setError('New assignee email is required.');
      return;
    }
    try {
      setWorking(true);
      setError(null);
      setMessage(null);
      await apiClient.post(`/api/admin/reviewmanagement/reviews/${reviewGuid}/reassign`, {
        newAssigneeEmail: newAssigneeEmail.trim(),
        note: actionNote.trim() || null,
        sessionGuid: localStorage.getItem('sessionGuid')
      });
      setMessage('Review reassigned successfully.');
      setActionNote('');
      await loadReview();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to reassign review');
    } finally {
      setWorking(false);
    }
  };

  const handleCancelConfirmed = async () => {
    if (!reviewGuid) return;
    setShowCancelModal(false);
    try {
      setWorking(true);
      setError(null);
      setMessage(null);
      await apiClient.post(`/api/admin/reviewmanagement/reviews/${reviewGuid}/cancel`, {
        note: actionNote.trim() || null,
        sessionGuid: localStorage.getItem('sessionGuid')
      });
      setMessage('Review cancelled successfully.');
      setActionNote('');
      await loadReview();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to cancel review');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading review details...</p>
      </Container>
    );
  }

  if (!review) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Review not found.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4 admin-review-detail-page">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div>
          <h2 className="mb-0">{review.reviewName}</h2>
          <div className="text-muted">Admin review detail</div>
        </div>
        <Link to="/admin/reviews">
          <Button variant="outline-secondary">Back to Reviews</Button>
        </Link>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert variant="success" dismissible onClose={() => setMessage(null)}>{message}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div>
              <strong>{review.templateName}</strong>
              <div className="text-muted small">{review.templateCategory}</div>
            </div>
            <Badge bg={review.isComplete ? 'success' : 'warning'}>{review.reviewStatus}</Badge>
          </div>
          <div className="small text-muted mb-1">Assigned To: {review.assignedTo}</div>
          <div className="small text-muted mb-1">Assigned By: {review.assignedBy}</div>
          <div className="small text-muted mb-1">Assigned Date: {review.assignedDate ? new Date(review.assignedDate).toLocaleString() : 'N/A'}</div>
          {review.dueDate && (
            <div className="small text-muted mb-1">Due Date: {new Date(review.dueDate).toLocaleDateString()}</div>
          )}
          <div className="small text-muted mb-2">Completed Date: {review.completedDate ? new Date(review.completedDate).toLocaleString() : 'N/A'}</div>
          {review.reviewDetails && (
            <Alert variant="light" className="mb-0">{review.reviewDetails}</Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><strong>Admin Actions</strong></Card.Header>
        <Card.Body>
          {!canMutate && (
            <Alert variant="info" className="mb-3">This review is completed/cancelled and can no longer be reassigned or cancelled.</Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Optional Note (used for reassign/cancel comments)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              disabled={working || !canMutate}
              placeholder="Optional note for audit trail and notifications"
            />
          </Form.Group>
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Reassign To Email</Form.Label>
                <Form.Control
                  type="email"
                  value={newAssigneeEmail}
                  onChange={(e) => setNewAssigneeEmail(e.target.value)}
                  disabled={working || !canMutate}
                />
              </Form.Group>
            </div>
            <div className="col-md-6 d-flex gap-2">
              <Button
                variant="primary"
                onClick={handleReassign}
                disabled={working || !canMutate || !newAssigneeEmail.trim()}
              >
                {working ? 'Working...' : <><FaExchangeAlt className="me-1" />Reassign</>}
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowCancelModal(true)}
                disabled={working || !canMutate}
              >
                <FaBan className="me-1" />Cancel Review
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><strong>Tasks ({review.tasks.length})</strong></Card.Header>
        <Card.Body>
          {review.tasks.length === 0 ? (
            <Alert variant="info" className="mb-0">No tasks found for this review.</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Task</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                {review.tasks.map((task) => (
                  <React.Fragment key={task.guid}>
                  <tr key={task.guid}>
                    <td>{task.sortOrder}</td>
                    <td>
                      <div className="fw-semibold">{task.taskName}</div>
                      {task.taskDescription && <div className="small text-muted">{task.taskDescription}</div>}
                    </td>
                    <td>{task.isRequired ? 'Yes' : 'No'}</td>
                    <td>
                      <Badge bg={task.isComplete ? 'success' : 'secondary'}>
                        {task.isComplete ? 'Complete' : 'Open'}
                      </Badge>
                    </td>
                    <td>
                      {task.commentCount > 0 ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setExpandedTaskGuid((current) => current === task.guid ? null : task.guid)}
                        >
                          {task.commentCount} {task.commentCount === 1 ? 'comment' : 'comments'}
                        </Button>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                  </tr>

                  {expandedTaskGuid === task.guid && task.comments.length > 0 && (
                    <tr>
                      <td colSpan={5} className="task-comments-row">
                        <div className="small fw-semibold mb-2">Task Comments</div>
                        <div className="d-flex flex-column gap-2">
                          {task.comments.map((comment) => (
                            <div key={comment.guid} className="border rounded p-2 task-comment-card">
                              <div className="small text-muted mb-1">
                                {comment.createdByEmail || 'Unknown'}
                                {' • '}
                                {comment.createdDate
                                  ? new Date(comment.createdDate).toLocaleString()
                                  : 'Unknown date'}
                              </div>
                              <div>{comment.commentText}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <div className="mt-4">
        <Button variant="outline-secondary" onClick={() => navigate('/admin/reviews')}>
          Back to Reviews
        </Button>
      </div>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel <strong>{review.reviewName}</strong>?</p>
          <p className="text-muted small mb-0">This marks the review as cancelled and cannot be undone.</p>
          {actionNote && (
            <Alert variant="secondary" className="mt-2 mb-0">
              <strong>Note will be recorded:</strong> {actionNote}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>Back</Button>
          <Button variant="danger" onClick={handleCancelConfirmed}>Confirm Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminReviewDetail;
